import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import BlogCategoryForm from "./BlogCategoryForm";

import { getBlogById, updateBlog } from "../../api/BlogCategoryService";
import { updateSeo } from "../../api/SeoService";
import FullPageLoader from "../../common/FullPageLoader";

const BlogCategoryedit = () => {
  const location = useLocation();

  const id = location.state?.blogId;
  const slug = location.state?.slug;
  const navigate = useNavigate();

  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setPageLoading(true);
      const res = await getBlogById(slug);
      const categoryData = res?.data || res;
      const seoData = categoryData?.seo || {};

      setCategory({
        _id: categoryData._id,
        name: categoryData.name || "",
        description: categoryData.description || "",
        status: categoryData.status || "ACTIVE",
        metaTitle: seoData.metaTitle || "",
        metaDescription: seoData.metaDescription || "",
        metaKeywords: Array.isArray(seoData.metaKeywords)
          ? seoData.metaKeywords.join(", ")
          : seoData.metaKeywords || "",
        metaImage: seoData.metaImage || null,
        metaImagePreview: seoData.metaImage || "",
      });
    } catch (error) {
      console.error(error);
      setCategory(null);
    } finally {
      setPageLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      setErrors({});

      const payload = {
        name: values.name,
        description: values.description,
        status: values.status,
      };

      const categoryRes = await updateBlog(id, payload);
      const categoryData = categoryRes?.data || categoryRes;
      const categoryId = categoryData?._id || categoryData?.id || id;

      if (!categoryId) {
        setErrors(categoryRes?.error || categoryRes?.data || { message: "Blog category update failed" });
        return;
      }

      const seoPayload = {
        referenceId: String(categoryId),
        referenceType: "BlogCategory",
        metaTitle: values.metaTitle || "",
        metaDescription: values.metaDescription || "",
        metaKeywords: Array.isArray(values.metaKeywords)
          ? values.metaKeywords
          : (values.metaKeywords || "")
              .split(",")
              .map((item) => item.trim())
              .filter(Boolean),
        status: values.status || "",
        metaImage:
          typeof values.metaImage === "string"
            ? values.metaImage
            : values.metaImage
              ? values.metaImage
              : "",
      };

      const seoRes = await updateSeo(seoPayload);
      const seoError =
        seoRes?.error || seoRes?.data?.error || seoRes?.data?.errors || seoRes?.errors;

      if (seoError || seoRes?.success === false) {
        setErrors(seoError || seoRes?.data || seoRes || { message: "Please fix SEO validation errors" });
        return;
      }

      navigate("/blogcategory");
    } catch (error) {
      console.error("UPDATE ERROR:", error);

      const responseData = error?.response?.data;
      const responseErrors =
        responseData?.error || responseData?.errors || responseData || error?.error || { message: error?.message || "Submit failed" };

      setErrors({
        ...(typeof responseErrors === "object" && responseErrors),
        message:
          responseData?.message ||
          responseErrors?.message ||
          error?.message ||
          "Submit failed",
      });
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) return <FullPageLoader text="Loading blog category..." />;
  if (!category) return <div className="container-fluid p-4">Blog category not found</div>;

  return (
    <BlogCategoryForm
      category={category}
      initialValues={category}
      onSubmit={handleSubmit}
      loading={loading}
      errors={errors}
      onCancel={() => navigate("/blogcategory")}
    />
  );
};

export default BlogCategoryedit;
